import { useMemo, type ReactNode } from 'react';
import { AlertCircle, CalendarClock, CreditCard, FileText, Receipt } from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { LocationBadge } from '@/components/shared/location-badge';
import { useAuthStore } from '@/stores/auth-store';
import { usePermissions } from '@/hooks/use-permissions';
import { useLocationStore } from '@/stores/location-store';
import { useRPDashboard, useRPInvoices } from '@/hooks/use-receive-payments';
import { useRecurringSales, RECURRING_FREQUENCY_LABELS } from '@/hooks/use-recurring-sales';
import { formatCurrency, formatNumber, formatShortDate } from '@/lib/utils';
import { ActivityList, CardSkeleton, KpiCard, Panel, PaymentSplit } from './home-cards';

const RP = '#/receive-payments';
const LIST_SHOWN = 5;

function customerLabel(c?: { name: string | null; company: string | null } | null): string {
  return c?.company || c?.name || 'Customer';
}

/**
 * The Receive Payments home: what's owed, what's overdue, what has come in,
 * and what's about to run. Every figure comes from the same receivepayment
 * endpoints the Receive Payments page uses, so the two always agree.
 *
 * The dashboard endpoint isn't date-filterable, so this page is a snapshot
 * ("right now") rather than a period view like the POS home.
 */
export function ReceivePaymentsHome({ switcher }: { switcher?: ReactNode }) {
  const { user } = useAuthStore();
  const { selectedLocationId } = useLocationStore();
  const locationId = selectedLocationId || undefined;
  const firstName = user?.name?.split(' ')[0] || 'there';

  const dashboard = useRPDashboard({ locationId });
  const dash = dashboard.data?.data;

  // Overdue invoices come straight off the invoice list: the dashboard only
  // carries the overdue total, not which invoices make it up.
  // The lists need their screen's View; without it the card is left out.
  const permissions = usePermissions();
  const canSeeInvoices = permissions.has('rp.invoices.view');
  const canSeeRecurring = permissions.has('rp.recurring.view');
  const overdue = useRPInvoices({ status: 'overdue', locationId, limit: LIST_SHOWN }, { enabled: canSeeInvoices });
  const recurring = useRecurringSales({ locationId, status: 'active', perPage: 100 }, { enabled: canSeeRecurring });

  const invoiceStatusRows = useMemo(() => {
    const rows = dash?.invoices.byStatus ?? [];
    const total = rows.reduce((sum, r) => sum + Number(r.amount || 0), 0);
    return {
      total,
      rows: rows
        .map((r) => ({
          method: r.status,
          total: Number(r.amount || 0),
          percent: total ? (Number(r.amount || 0) / total) * 100 : 0,
        }))
        .filter((r) => r.total > 0)
        .sort((a, b) => b.total - a.total),
    };
  }, [dash]);

  const methodRows = useMemo(() => {
    const rows = dash?.payments.byMethod ?? [];
    const total = rows.reduce((sum, r) => sum + Number(r.amount || 0), 0);
    return {
      total,
      rows: rows
        .map((r) => ({
          method: r.method,
          total: Number(r.amount || 0),
          percent: total ? (Number(r.amount || 0) / total) * 100 : 0,
        }))
        .filter((r) => r.total > 0)
        .sort((a, b) => b.total - a.total),
    };
  }, [dash]);

  const upcoming = useMemo(() => {
    const rows = recurring.data?.data ?? [];
    return [...rows]
      .sort((a, b) => Date.parse(a.nextRunAt) - Date.parse(b.nextRunAt))
      .slice(0, LIST_SHOWN)
      .map((r) => ({
        id: r.id,
        primary: r.businessName || [r.firstName, r.lastName].filter(Boolean).join(' ') || 'Customer',
        secondary: r.frequency === 'once' ? 'One-time' : RECURRING_FREQUENCY_LABELS[r.frequency],
        value: formatCurrency(r.amount),
        meta: `Next ${formatShortDate(r.nextRunAt)}`,
      }));
  }, [recurring.data]);

  const openInvoices = (dash?.invoices.byStatus ?? [])
    .filter((r) => !['paid', 'void', 'draft'].includes(r.status.toLowerCase()))
    .reduce((sum, r) => sum + r.count, 0);

  return (
    <div className="space-y-6">
      <PageHeader title={`Welcome back, ${firstName}`} description="How your receivables are doing right now">
        <LocationBadge />
        {switcher}
      </PageHeader>

      {!selectedLocationId && (
        <div className="rounded-xl border border-border bg-white p-8 text-center text-sm text-muted-foreground">
          Choose a location from the switcher above to see its numbers.
        </div>
      )}

      {/* ── headline figures ─────────────────────────────────────────── */}
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {dashboard.isLoading || !dash ? (
          <>
            <CardSkeleton height="h-36" />
            <CardSkeleton height="h-36" />
            <CardSkeleton height="h-36" />
            <CardSkeleton height="h-36" />
          </>
        ) : (
          <>
            <KpiCard
              label="Total Receivable"
              value={formatCurrency(dash.invoices.totalReceivable)}
              footnote={`${formatNumber(openInvoices)} open invoices`}
              series={[]}
              dataKey="value"
            />
            <KpiCard
              label="Overdue"
              value={formatCurrency(dash.invoices.overdue)}
              footnote={canSeeInvoices ? `${formatNumber(overdue.data?.total ?? 0)} overdue invoices` : 'Past due date'}
              series={[]}
              dataKey="value"
              invert
            />
            <KpiCard
              label="Collected"
              value={formatCurrency(dash.invoices.collected)}
              footnote={`${formatNumber(dash.payments.total)} payments received`}
              series={[]}
              dataKey="value"
            />
            <KpiCard
              label="Invoices"
              value={formatNumber(dash.invoices.total)}
              footnote={`${formatNumber(dash.customers.active)} active customers`}
              series={[]}
              dataKey="value"
            />
          </>
        )}
      </div>

      {/* ── mix ───────────────────────────────────────────────────────── */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Invoices by Status" href={`${RP}?tab=invoices`} requires="rp.invoices.view">
          {dashboard.isLoading ? (
            <CardSkeleton height="h-40" />
          ) : (
            <PaymentSplit
              rows={invoiceStatusRows.rows}
              total={invoiceStatusRows.total}
              empty={{
                icon: FileText,
                title: 'No invoices yet',
                description: 'Invoice amounts split by status appear here.',
              }}
            />
          )}
        </Panel>
        <Panel title="Payments by Method" href={`${RP}?tab=payments`} requires="rp.transactions.view">
          {dashboard.isLoading ? (
            <CardSkeleton height="h-40" />
          ) : (
            <PaymentSplit
              rows={methodRows.rows}
              total={methodRows.total}
              empty={{
                icon: CreditCard,
                title: 'No payments yet',
                description: 'Card, ACH and other methods appear here as a split.',
              }}
            />
          )}
        </Panel>
      </div>

      {/* ── activity ──────────────────────────────────────────────────── */}
      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Recent Payments" href={`${RP}?tab=payments`} requires="rp.transactions.view">
          {dashboard.isLoading ? (
            <CardSkeleton height="h-40" />
          ) : (
            <ActivityList
              rows={(dash?.recentPayments ?? []).map((p) => ({
                id: p.id,
                primary: customerLabel(p.customer),
                secondary: p.invoice?.invoiceNumber ? `Invoice ${p.invoice.invoiceNumber}` : p.referenceNumber,
                value: formatCurrency(p.amount),
                status: p.status,
                meta: formatShortDate(p.receivedAt || p.createdAt),
              }))}
              empty={{
                icon: Receipt,
                title: 'No payments yet',
                description: 'The latest customer payments show up here.',
              }}
            />
          )}
        </Panel>
        {canSeeInvoices && (
          <Panel title="Overdue Invoices" href={`${RP}?tab=invoices`} requires="rp.invoices.view">
            {overdue.isLoading ? (
              <CardSkeleton height="h-40" />
            ) : (
              <ActivityList
                rows={(overdue.data?.data ?? []).map((inv) => ({
                  id: inv.id,
                  primary: customerLabel(inv.customer),
                  secondary: `Invoice ${inv.invoiceNumber}`,
                  value: formatCurrency(inv.amount),
                  meta: inv.dueDate ? `Due ${formatShortDate(inv.dueDate)}` : undefined,
                  alert: true,
                }))}
                empty={{
                  icon: AlertCircle,
                  title: 'Nothing overdue',
                  description: 'Invoices past their due date are listed here.',
                }}
              />
            )}
          </Panel>
        )}
      </div>

      {canSeeRecurring && (
        <div className="grid gap-4 lg:grid-cols-2">
          <Panel title="Upcoming Recurring Sales" href={`${RP}?tab=recurring`} requires="rp.recurring.view">
            {recurring.isLoading ? (
              <CardSkeleton height="h-40" />
            ) : (
              <ActivityList
                rows={upcoming}
                empty={{
                  icon: CalendarClock,
                  title: 'No recurring sales scheduled',
                  description: 'Active recurring sales appear here by next run date.',
                }}
              />
            )}
          </Panel>
        </div>
      )}
    </div>
  );
}
